package br.com.vvrefeicoes.portal.feature.auth.login

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppBanner
import br.com.vvrefeicoes.portal.core.ui.components.AppFooter
import br.com.vvrefeicoes.portal.core.ui.components.AppLogo
import br.com.vvrefeicoes.portal.core.ui.layout.AppResponsiveLayout
import br.com.vvrefeicoes.portal.core.ui.layout.AppScrollColumn
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.auth_hello_collaborator
import portalvv.shared.generated.resources.auth_password_changed_success
import portalvv.shared.generated.resources.slogan
import portalvv.shared.generated.resources.undrawable_system_interface

private object LoginLayoutDefaults {
    val formMaxWidth = 420.dp
    const val ILLUSTRATION_ASPECT_RATIO = 480f / 354f
}

@Composable
actual fun LoginLayout(controller: LoginController) {
    AppResponsiveLayout(modifier = Modifier.fillMaxSize()) { isCompact ->
        if (isCompact) {
            CompactLoginLayout(controller)
        } else {
            WideLoginLayout(controller)
        }
    }
}

@Composable
private fun CompactLoginLayout(controller: LoginController) {
    AppScrollColumn(
        modifier = Modifier.fillMaxSize(),
        contentPadding = PaddingValues(AppSpacing.space24),
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space24),
        horizontalAlignment = Alignment.CenterHorizontally,
        footer = { AppFooter() },
    ) {
        Column(
            modifier =
                Modifier
                    .widthIn(max = LoginLayoutDefaults.formMaxWidth)
                    .fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space24),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            AppLogo()
            Text(
                text = stringResource(Res.string.slogan),
                style = MaterialTheme.typography.titleLarge,
                color = MaterialTheme.colorScheme.onBackground,
            )
            LoginIllustration(
                modifier =
                    Modifier
                        .widthIn(max = 298.dp)
                        .fillMaxWidth(0.9f),
            )
            if (controller.showPasswordChangedBanner) {
                AppBanner(
                    text = stringResource(Res.string.auth_password_changed_success),
                    onDismiss = controller::dismissPasswordChangedBanner,
                )
            }
            LoginForm(controller = controller)
        }
    }
}

@Composable
private fun WideLoginLayout(controller: LoginController) {
    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        val minHeight = maxHeight
        AppScrollColumn(modifier = Modifier.fillMaxSize()) {
            Row(
                modifier =
                    Modifier
                        .fillMaxWidth()
                        .heightIn(min = minHeight),
            ) {
                Column(
                    modifier =
                        Modifier
                            .weight(1f)
                            .heightIn(min = minHeight)
                            .background(MaterialTheme.colorScheme.background)
                            .padding(start = AppSpacing.space32, top = AppSpacing.space32, end = AppSpacing.space32),
                    verticalArrangement = Arrangement.SpaceBetween,
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    Column(
                        verticalArrangement = Arrangement.spacedBy(AppSpacing.space16),
                        horizontalAlignment = Alignment.CenterHorizontally,
                    ) {
                        AppLogo()
                        Text(
                            text = stringResource(Res.string.slogan),
                            style = MaterialTheme.typography.titleLarge,
                            color = MaterialTheme.colorScheme.onBackground,
                        )
                    }
                    LoginIllustration(
                        modifier =
                            Modifier
                                .widthIn(max = 480.dp)
                                .fillMaxWidth(0.9f),
                    )
                    AppFooter()
                }

                Column(
                    modifier =
                        Modifier
                            .weight(1f)
                            .heightIn(min = minHeight)
                            .padding(vertical = AppSpacing.space32, horizontal = AppSpacing.space48),
                    verticalArrangement = Arrangement.spacedBy(AppSpacing.space48),
                    horizontalAlignment = Alignment.Start,
                ) {
                    if (controller.showPasswordChangedBanner) {
                        AppBanner(
                            text = stringResource(Res.string.auth_password_changed_success),
                            onDismiss = controller::dismissPasswordChangedBanner,
                        )
                    }
                    Text(
                        text = stringResource(Res.string.auth_hello_collaborator),
                        style = MaterialTheme.typography.headlineMedium,
                    )
                    LoginForm(controller = controller)
                }
            }
        }
    }
}

@Composable
private fun LoginIllustration(modifier: Modifier = Modifier) {
    Image(
        painter = painterResource(Res.drawable.undrawable_system_interface),
        contentDescription = null,
        modifier = modifier.aspectRatio(LoginLayoutDefaults.ILLUSTRATION_ASPECT_RATIO),
        contentScale = ContentScale.Fit,
    )
}
